/**
 * Premium / Plans page — the single checkout entry point on web.
 *
 * Free/Lite users: shows benefits + one checkout CTA using the current plan terms.
 * Trial users: trial status + subscribe CTA.
 * Premium users: current plan status + manage subscription link.
 *
 * All per-feature PremiumGate components on other screens link here instead of
 * hosting their own inline checkout buttons (05-ux-standards cohesion).
 */

import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import {
  AlreadyPremiumError,
  formatMonthlyPrice,
  usePremiumPlan,
  useStartCheckout,
} from '../hooks/useBilling.js';
import { useEntitlement } from '../hooks/useEntitlement.js';

function daysUntil(isoDate: string): number {
  return Math.max(0, Math.ceil((new Date(isoDate).getTime() - Date.now()) / 86_400_000));
}

export default function PremiumPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('paywall');
  const benefits = t('benefits', { returnObjects: true }) as string[];

  const { data: entitlement, isLoading: entitlementLoading } = useEntitlement();
  const { data: plan } = usePremiumPlan();
  const checkout = useStartCheckout();

  const isPremium = entitlement?.isPremium ?? false;
  const inTrial = entitlement?.inTrial ?? false;
  const trialEligible = entitlement?.trialEligibility === 'eligible';
  const price = formatMonthlyPrice(plan);
  const alreadyPremium = checkout.error instanceof AlreadyPremiumError;
  const trialDays = plan?.trialDays ?? 0;

  const ctaLabel =
    trialEligible && trialDays > 0
      ? `Start your ${trialDays}-day free trial`
      : price
        ? `Upgrade to Premium — ${price}`
        : 'Upgrade to Premium';

  const trialDaysLeft =
    inTrial && entitlement?.currentPeriodEnd ? daysUntil(entitlement.currentPeriodEnd) : null;

  function handleBack() {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  }

  return (
    <div className="flex flex-col gap-8 max-w-sm">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-semibold tracking-tight text-ink">{t('title')}</h2>
        <p className="mt-1 text-sm text-ink-soft">
          {isPremium && !inTrial ? t('subtitlePremium') : t('subtitleTrial')}
        </p>
      </div>

      {/* Benefits list */}
      <ul className="flex flex-col gap-2.5" aria-label="Premium benefits">
        {benefits.map((benefit) => (
          <li key={benefit} className="flex items-start gap-3 text-sm text-ink">
            <span className="mt-0.5 shrink-0 text-leaf" aria-hidden>
              ✓
            </span>
            {benefit}
          </li>
        ))}
      </ul>

      {/* State-specific content */}
      {entitlementLoading ? (
        <p className="text-sm text-ink-soft">Loading…</p>
      ) : isPremium ? (
        /* ── Premium or active trial ───────────────────────────── */
        <div className="flex flex-col gap-3">
          {inTrial ? (
            <div className="rounded-xl border border-mist/20 bg-mist/10 px-4 py-3">
              <p className="text-sm font-semibold text-leaf">{t('trialActive')}</p>
              {trialDaysLeft !== null && (
                <p className="mt-0.5 text-xs text-leaf">
                  {t('trialDaysLeft', { count: trialDaysLeft })}
                </p>
              )}
            </div>
          ) : (
            <div className="rounded-xl border border-leaf/20 bg-leaf/10 px-4 py-3">
              <p className="text-sm font-semibold text-leaf">{t('alreadyPremium')}</p>
              {entitlement?.currentPeriodEnd && (
                <p className="mt-0.5 text-xs text-leaf">
                  {entitlement.willRenew
                    ? `Renews ${new Date(entitlement.currentPeriodEnd).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}`
                    : `Expires ${new Date(entitlement.currentPeriodEnd).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}`}
                </p>
              )}
              {entitlement?.inGracePeriod && (
                <p className="mt-1 text-xs text-clay">{t('gracePeriod')}</p>
              )}
            </div>
          )}

          {entitlement?.managementUrl && (
            <a
              href={entitlement.managementUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-block rounded-xl border border-husk px-4 py-2 text-sm font-medium
                         text-ink hover:bg-husk/50 focus:outline-none focus:ring-2 focus:ring-leaf"
            >
              {t('manageSubscription')}
            </a>
          )}

          <button
            type="button"
            onClick={handleBack}
            className="text-sm text-ink-soft hover:text-ink focus:outline-none"
          >
            {t('done')}
          </button>
        </div>
      ) : (
        /* ── Free / Lite ────────────────────────────────────────── */
        <div className="flex flex-col gap-4">
          <button
            type="button"
            onClick={() => checkout.mutate({})}
            disabled={checkout.isPending}
            className="rounded-xl bg-leaf px-5 py-3 text-sm font-semibold text-white
                       hover:bg-leaf-deep focus:outline-none focus:ring-2 focus:ring-leaf
                       focus:ring-offset-2 disabled:opacity-60"
          >
            {checkout.isPending ? 'Starting checkout…' : ctaLabel}
          </button>

          {trialEligible && trialDays > 0 && (
            <p className="text-xs text-ink-soft">{t('trial.subtitle')}</p>
          )}

          {alreadyPremium && (
            <p className="text-xs text-ink-soft" role="status">
              You&apos;re already on Premium. Check{' '}
              <Link to="/settings#plan" className="underline">
                Settings
              </Link>{' '}
              to manage your subscription.
            </p>
          )}

          {checkout.isError && !alreadyPremium && (
            <p className="text-xs text-clay" role="alert">
              Could not start checkout. Please try again.
            </p>
          )}

          <button
            type="button"
            onClick={handleBack}
            className="text-sm text-ink-soft hover:text-ink focus:outline-none"
          >
            {t('dismiss')}
          </button>
        </div>
      )}
    </div>
  );
}
